// frontend/utils/socket.ts
// Socket.IO client singleton for the DicePit game server

import { io, Socket } from 'socket.io-client';
import { supabase } from './supabase';

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3001';

let socket: Socket | null = null;
let connectPromise: Promise<Socket> | null = null;

// ── Instant disconnect on tab close ──────────────────────────────────────────

function handleBeforeUnload() {
  if (socket?.connected) {
    socket.emit('leave_room');
    socket.disconnect();
  }
}

// Register once — idempotent (adding same ref twice is a no-op in practice,
// but we guard with a flag just in case).
let beforeUnloadRegistered = false;
function ensureBeforeUnload() {
  if (beforeUnloadRegistered) return;
  window.addEventListener('beforeunload', handleBeforeUnload);
  beforeUnloadRegistered = true;
}
function removeBeforeUnload() {
  window.removeEventListener('beforeunload', handleBeforeUnload);
  beforeUnloadRegistered = false;
}

/**
 * Get or create a Socket.IO connection to the game server.
 * Returns the same singleton socket for all callers.
 * Uses the current Supabase session token for authentication.
 */
export async function getSocket(): Promise<Socket> {
  // Return existing connected socket
  if (socket?.connected) return socket;

  // Return existing socket that's still connecting (not disconnected)
  if (socket && !socket.disconnected) return socket;

  // Avoid multiple concurrent connection attempts
  if (connectPromise) return connectPromise;

  connectPromise = (async () => {
    try {
      // Wait for auth session to be available (may not be immediate after anonymous sign-in)
      let token: string | undefined;
      for (let attempt = 0; attempt < 10; attempt++) {
        const { data: { session } } = await supabase.auth.getSession();
        token = session?.access_token;
        if (token) break;
        console.log(`[socket] No session yet, retrying (${attempt + 1}/10)...`);
        await new Promise((r) => setTimeout(r, 300));
      }

      if (!token) {
        throw new Error('No auth session. Please sign in first.');
      }

      // Disconnect existing stale socket
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
        socket = null;
      }

      socket = io(WS_URL, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
      });

      return new Promise<Socket>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Connection timeout'));
        }, 10000);

        socket!.once('connect', () => {
          clearTimeout(timeout);
          console.log('[socket] Connected to game server');
          ensureBeforeUnload();
          resolve(socket!);
        });

        socket!.once('connect_error', (err) => {
          clearTimeout(timeout);
          console.error('[socket] Connection error:', err.message);
          reject(err);
        });
      });
    } finally {
      connectPromise = null;
    }
  })();

  return connectPromise;
}

/**
 * Disconnect the socket (on logout, leave, etc.)
 */
export function disconnectSocket(): void {
  removeBeforeUnload();
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  connectPromise = null;
}

/**
 * Get the current socket instance (may be null if not connected).
 */
export function getCurrentSocket(): Socket | null {
  return socket;
}
